import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FilterService } from '../../../../../../Common/Filter/services/abstract-filter.service';
import { FilterTypeCheckboxModel } from '../../../../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../../../../../../Common/Filter/models/filter-type-date-query.model';
import { getDefaultDateInputOptionValuesModel } from '../../../../../../Common/Forms/models/date-query-input-options.model';
import { EventsApi } from '../../../../../../api/events/events-api';
import { EventModel } from '../../../../models/event.model';
import { DateQueryModel } from '../../../../../../Common/Forms/models/date-query.model';
import { ActivityTypeEnum, PasswordEventTypeEnum } from './activity-log.enum';

export interface CardModel extends EventModel {
    isExpanded: boolean;
    detailsObj: {
        reason?: string;
        method?: string;
        userIP?: string;
        event?: string;
        resourceName?: string;
        diff?: Record<string, string>;
    };
    specificAction?: string;
}

export interface LoginStatsModel {
    lastLoginDate?: Date;
    recentLoginCount: number;
}

@Injectable()
export class ActivityLogService extends FilterService {
    flag = 0;

    private activities = new BehaviorSubject<CardModel[]>([]);
    public activities$ = this.activities.asObservable();

    private filteredActivities = new BehaviorSubject<CardModel[]>([]);
    public filteredActivities$ = this.filteredActivities.asObservable();

    private loginStats = new BehaviorSubject<LoginStatsModel>({recentLoginCount: 0});
    public loginStats$ = this.loginStats.asObservable();

    private readonly recentLoginWindowDays = 30;
    private readonly resourceCodePattern = /^[A-Z0-9]+(_[A-Z0-9]+)+$/;

    private readonly activityTypeFilter = new FilterTypeCheckboxModel<ActivityTypeEnum | PasswordEventTypeEnum>(
        'ACTION_TYPE',
        'UMT.activityLog.activityType',
        [
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.LOGIN,
                value: ActivityTypeEnum.LOGIN
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.LOGIN_ERROR,
                value: ActivityTypeEnum.LOGIN_ERROR
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.UPDATE,
                value: ActivityTypeEnum.UPDATE
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.POST,
                value: ActivityTypeEnum.POST
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.PUT,
                value: ActivityTypeEnum.PUT
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.DELETE,
                value: ActivityTypeEnum.DELETE
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.CREATE,
                value: ActivityTypeEnum.CREATE
            },
            {
                label: 'UMT.activityLog.activityType.' + ActivityTypeEnum.NEPTUNE_SSO_OAUTH_REDIRECT,
                value: ActivityTypeEnum.NEPTUNE_SSO_OAUTH_REDIRECT
            },
            {
                label: 'UMT.activityLog.activityType.' + PasswordEventTypeEnum.FORGOT_PASSWORD_REQUESTED,
                value: PasswordEventTypeEnum.FORGOT_PASSWORD_REQUESTED
            },
            {
                label: 'UMT.activityLog.activityType.' + PasswordEventTypeEnum.PASSWORD_RESET_SUCCESS,
                value: PasswordEventTypeEnum.PASSWORD_RESET_SUCCESS
            },
            {
                label: 'UMT.activityLog.activityType.' + PasswordEventTypeEnum.PASSWORD_CHANGED,
                value: PasswordEventTypeEnum.PASSWORD_CHANGED
            },
        ]
    );

    private readonly createdDateFilter = new FilterTypeDateQueryModel(
        'createdDate',
        'dateCreated',
        getDefaultDateInputOptionValuesModel()
    );

    constructor(
        public eventsApi: EventsApi,
    ) {
        super();
        this.filters.push(
            this.activityTypeFilter,
            this.createdDateFilter
        );
        this.filters$.next(this.filters);
        this.applyFilters();
    }

    applyFilters(): void {
        super.applyFilters();
        this.getFilteredActivities();
    }

    getActivities(userId: number): void {
        this.eventsApi.searchAny({userId}).subscribe({
            next: events => {
                const activities = events
                    .map(event => {
                        const details = JSON.parse(event.details);
                        delete details.oauthSsoResult;
                        const activity: CardModel = Object.assign(
                            new EventModel(event),
                            {
                                detailsObj: details,
                                isExpanded: false,
                                specificAction: this.getSpecificAction(details)
                            }
                        );
                        // Show password events with a explicit activity log card
                        if (Object.values(PasswordEventTypeEnum).includes(details?.eventType as PasswordEventTypeEnum)) {
                            activity.action = details.eventType;
                        }
                        return activity;
                    })
                    .sort((x1, x2) => x2.date.getTime() - x1.date.getTime());

                this.activities.next(activities);
                this.loginStats.next(this.getLoginStats(activities));
                this.flag += 1;
                this.getFilteredActivities();
            },
            error: error => {
                console.error(error);
            }
        });
    }

    private getLoginStats(activities: CardModel[]): LoginStatsModel {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - this.recentLoginWindowDays);

        const logins = activities.filter(activity => activity.action === ActivityTypeEnum.LOGIN);

        return {
            lastLoginDate: logins[0]?.date,
            recentLoginCount: logins.filter(login => !!login.date && login.date >= cutoff).length
        };
    }

    private getSpecificAction(detailsObj: CardModel['detailsObj']): string | undefined {
        if (detailsObj?.event) {
            return this.humanizeCode(detailsObj.event);
        }
        if (detailsObj?.resourceName && this.resourceCodePattern.test(detailsObj.resourceName)) {
            return this.humanizeCode(detailsObj.resourceName);
        }
        if (detailsObj?.diff) {
            const fields = Object.keys(detailsObj.diff).map(field => this.humanizeCode(field));
            if (fields.length > 0) {
                return `Updated ${fields.join(', ')}`;
            }
        }
        return undefined;
    }

    private humanizeCode(value: string): string {
        return value
            .split('_')
            .filter(word => word.length > 0)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    getFilteredActivities(): void {

        const activityTypeFilters = this.activityTypeFilter.getValue();
        const createdDateFilter = this.createdDateFilter.getValue();
        const activities = this.activities.getValue().slice();

        const filteredActivities = activities.filter(activity => {
            if(activityTypeFilters && activityTypeFilters.length > 0) {
                if(!activityTypeFilters.includes(activity?.action as ActivityTypeEnum | PasswordEventTypeEnum)){
                    return false;
                }
            }
            if (createdDateFilter) {
                if (!DateQueryModel.isInDateQuery(activity.date as Date, createdDateFilter, new Date(), 30)) {
                    return false;
                }
            }
            return true;
        });

        this.filteredActivities.next(filteredActivities);
    }
}
